import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  forwardRef,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';

import { CreateBuildingRequest } from '@core/api/models';
import { BuildingAddressListEditorComponent } from '@features/building/shared/components/building-address-list-editor/building-address-list-editor.component';
import {
  BuildingAddressFormGroup,
  createBuildingAddressFormArray,
  createBuildingAddressFormGroup,
  toBuildingAddressRequest,
} from '@features/building/shared/utils/building-address-form.utils';

type InvestmentBuildingFormGroup = FormGroup<{
  buildingCode: FormControl<string>;
  addresses: FormArray<BuildingAddressFormGroup>;
}>;

@Component({
  selector: 'app-investment-buildings-editor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BuildingAddressListEditorComponent,
    ButtonModule,
    InputTextModule,
    MessageModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InvestmentBuildingsEditorComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InvestmentBuildingsEditorComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './investment-buildings-editor.component.html',
  styleUrls: ['./investment-buildings-editor.component.scss']
})
export class InvestmentBuildingsEditorComponent implements ControlValueAccessor, Validator {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  private onChange: (value: CreateBuildingRequest[]) => void = () => { };
  private onTouched: () => void = () => { };
  private onValidatorChange: () => void = () => { };
  private isWritingValue = false;

  readonly form = this.fb.group({
    buildings: this.fb.array<InvestmentBuildingFormGroup>([])
  });

  disabled = false;

  constructor() {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isWritingValue) {
          return;
        }

        this.onTouched();
        this.onChange(this.readFormValue());
        this.onValidatorChange();
      });

    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onValidatorChange();
      });
  }

  get buildingsArray(): FormArray<InvestmentBuildingFormGroup> {
    return this.form.controls.buildings;
  }

  getAddressesArray(buildingIndex: number): FormArray<BuildingAddressFormGroup> {
    return this.buildingsArray.at(buildingIndex).controls.addresses;
  }

  isControlInvalid(control: AbstractControl | null): boolean {
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  writeValue(value: CreateBuildingRequest[] | null): void {
    const normalized = this.normalizeIncomingValue(value);

    this.isWritingValue = true;
    this.buildingsArray.clear({ emitEvent: false });
    normalized.forEach((building) => {
      this.buildingsArray.push(this.createBuildingGroup(building), { emitEvent: false });
    });
    this.isWritingValue = false;
    this.onValidatorChange();
  }

  registerOnChange(fn: (value: CreateBuildingRequest[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
    this.onValidatorChange();
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.disabled) {
      return null;
    }

    if (this.form.invalid) {
      return { custom: true };
    }

    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  addBuilding(): void {
    this.buildingsArray.push(this.createBuildingGroup());
    this.markTouchedAndEmit();
  }

  removeBuilding(index: number): void {
    this.buildingsArray.removeAt(index);
    this.markTouchedAndEmit();
  }

  addAddress(buildingIndex: number): void {
    this.getAddressesArray(buildingIndex).push(createBuildingAddressFormGroup(this.fb));
    this.markTouchedAndEmit();
  }

  removeAddress(buildingIndex: number, addressIndex: number): void {
    const addresses = this.getAddressesArray(buildingIndex);
    if (addresses.length <= 1) {
      return;
    }

    addresses.removeAt(addressIndex);
    this.markTouchedAndEmit();
  }

  private markTouchedAndEmit(): void {
    this.onTouched();
    this.onChange(this.readFormValue());
    this.onValidatorChange();
  }

  private createBuildingGroup(value?: Partial<CreateBuildingRequest>): InvestmentBuildingFormGroup {
    const addresses = Array.isArray(value?.addresses) && value.addresses.length > 0
      ? value.addresses
      : [{}];

    return this.fb.group({
      buildingCode: this.fb.control(value?.buildingCode ?? '', { validators: [Validators.required], nonNullable: true }),
      addresses: createBuildingAddressFormArray(this.fb, addresses),
    });
  }

  private normalizeIncomingValue(value: CreateBuildingRequest[] | null): CreateBuildingRequest[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((building) => ({
      buildingCode: building?.buildingCode ?? '',
      addresses: Array.isArray(building?.addresses) && building.addresses.length > 0
        ? building.addresses.map((address) => ({
          buildingNumber: address?.buildingNumber ?? '',
          entranceCode: address?.entranceCode ?? ''
        }))
        : [{ buildingNumber: '', entranceCode: '' }]
    }));
  }

  private readFormValue(): CreateBuildingRequest[] {
    return this.buildingsArray.controls.map((buildingControl) => {
      const buildingValue = buildingControl.getRawValue();

      return {
        buildingCode: buildingValue.buildingCode?.trim() ?? '',
        addresses: (buildingValue.addresses ?? []).map((address) => toBuildingAddressRequest(address))
      };
    });
  }
}
